import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { autoAuctionApiSdk } from '../sdk/autoAuctionClient.js';

// This module used to set `baseAutoAuctionUri = "http://localhost:4500"` right
// here, at import time. It survived only because main.js configures the client
// in its own body, which runs after every import has been evaluated — so the
// hardcoded origin was overwritten a moment later, every time, by luck. Move
// the assignment in main.js above an import and a production build starts
// talking to the developer's laptop. Configuration belongs to the host.

export const useVehicleSearchStore = defineStore('vehicleSearchStore', () => {
    const orderedDependencyCriterion = ['MAKE', 'MODEL', 'SERIES', 'BODYSTYLE'];

    /**********************************************/
    /*   START VISIBLE ACTIVE CRITERION           */
    /**********************************************/
    const activeCriterion = ref([]);
    const getActiveCriterion = computed(() => { return activeCriterion });
    async function addActiveCriterion(criterionName) {
        activeCriterion.value.push(criterionName);
        loadCriterionOptions(criterionName);
    }
    function removeActiveCriterion(criterionName) {
        activeCriterion.value = activeCriterion.value.filter((name) => name != criterionName);
    }

    /**********************************************/
    /*   END VISIBLE ACTIVE CRITERION             */
    /*   START OPTIONS                            */
    /**********************************************/
    const optionsByCriterion = ref({
        MAKE: [],
        MODEL: [],
        SERIES: [],
        BODYSTYLE: []
    });
    const getOptionsByCriterion = computed(() => {
        return (criterionName) => { return optionsByCriterion.value[criterionName] }
    })

    /**********************************************/
    /*   END OPTIONS                              */
    /*   START SELECTIONS                         */
    /**********************************************/
    const selectionsByCriterion = ref({
        MAKE: [],
        MODEL: [],
        SERIES: [],
        BODYSTYLE: []
    })
    const getSelectionsByCriterion = computed(() => {
        return (criterionName) => { return selectionsByCriterion.value[criterionName] }
    })
    const getSearchBySelections = computed(() => {
        const search = {}
        const selectionNames = Object.keys(selectionsByCriterion.value);
        // Every tracked criterion is one of the four in `SearchCriterionName`, so
        // the switch that used to spell them out one case at a time collapses to
        // this single branch. A future non-dependency criterion (YEAR, lights)
        // that sends its raw selections rather than option values needs its own
        // branch adding back here.
        selectionNames.forEach((selectionName) => {
            if (selectionsByCriterion.value[selectionName].length > 0){
                search[selectionName] = selectionsByCriterion.value[selectionName].map((selectionObj) => { return selectionObj.option })
            }
        })
        return search;
    })
    function addCriterionSelection(criterion, selectionValue) {
        /*
            On an add,
                - isDependencyCriterion = the added criterion is part of the dependency chain
                - iterate through all 'active' criterion
                    - if isDependencyCriterion (ex: SERIES)
                        - do nothing if it is itself
                        - re-load options if direct downstream and active
                        - re-load options if not a dependency criterion
                    - else (ex: YEAR)
                        - do nothing if it is itself
                        - otherwise re-load options
        */
        selectionsByCriterion.value[criterion].push(selectionValue);
        const isDependencyCriterion = orderedDependencyCriterion.includes(criterion);

        // Determine which options to re-load
        activeCriterion.value.forEach((activeCrit) => {
            if (criterion === activeCrit) return;
            if (isDependencyCriterion && orderedDependencyCriterion.includes(criterion)) {
                const index = orderedDependencyCriterion.indexOf(criterion);
                const activeIndex = orderedDependencyCriterion.indexOf(activeCrit);
                if (index + 1 === activeIndex) {  // If it is the next immediate downstream dependency...
                    loadCriterionOptions(activeCrit);
                }
            } else {
                loadCriterionOptions(activeCrit);
            }
        })

        console.log(`Store added selection: ${criterion} - ${selectionValue.option}`)
        console.log(JSON.stringify(selectionsByCriterion.value[criterion], null, 2))
    }
    function removeCriterionSelection(criterion, selectionValue) {
        /*
            On a remove,
                - isDependencyCriterion = the added criterion is part of the dependency chain
                if (isDependencyCriterion)
                    - all downstream options based on remove criterion need to be deleted:
                        - ex: unselect MAKE: 'Toyota', deletes all options in MODEL/SERIES/BODYSTYLE that match MAKE
                        - no need to re-load options in dependency criterion
                        - all other options (if it uses counts, like lights ) need to be reloaded
                else
                    - all active options need to be reloaded
        */
        selectionsByCriterion.value[criterion] = selectionsByCriterion.value[criterion].filter((selectionObj) => selectionObj.option != selectionValue.option) || [];

        // Handle downstream changes (if any)
        const isDependencyCriterion = orderedDependencyCriterion.includes(criterion);
        if (isDependencyCriterion) {
            const startingIndex = orderedDependencyCriterion.indexOf(criterion) + 1;
            for (let i = startingIndex; i < orderedDependencyCriterion.length; i++) {
                const criterionGroup = orderedDependencyCriterion[i];
                if (criterionGroup) {
                    // Remove all options that match the selection
                    const filteredOptions = optionsByCriterion.value[criterionGroup].filter((opt) => { return opt[criterion] != selectionValue.option })
                    const filteredSelections = selectionsByCriterion.value[criterionGroup].filter((opt) => { return opt[criterion] != selectionValue.option })
                    optionsByCriterion.value[criterionGroup] = filteredOptions;
                    selectionsByCriterion.value[criterionGroup] = filteredSelections;
                }
            }
        }
        activeCriterion.value.forEach((activeCrit) => {
            if (selectionsByCriterion.value[activeCrit].length ===  0 ){
                    removeActiveCriterion(activeCrit);
            }
            if (!orderedDependencyCriterion.includes(activeCrit)) {
                loadCriterionOptions(activeCrit); // Reload all non-ordered dependency options.
            }
        })

        console.log(`Store removed selection: ${criterion} - ${selectionValue.option}`);
        console.log(`${JSON.stringify(selectionsByCriterion.value[criterion], null, 2)}`);
        // TODO: If
    }

    /**********************************************/
    /*   END SELECTIONS                           */
    /*   START LOADING CRITERION                  */
    /**********************************************/
    const loadingByCriterion = ref({
        MAKE: false,
        MODEL: false,
        SERIES: false,
        BODYSTYLE: false
    })
    const getLoadingByCriterion = computed(() => {
        return (criterionName) => { return loadingByCriterion.value[criterionName] }
    })
    function setLoadingByCriterion(criterion, isLoading) {
        loadingByCriterion.value[criterion] = isLoading;
    }

    // Make the clientApi call to grab options.
    async function loadCriterionOptions(criterionName) {
        // autoAuctionApiSdk. do the thang.
        console.log(`Loading option: ${criterionName}: Selections: ${JSON.stringify(selectionsByCriterion.value, null, 2)}`)
        setLoadingByCriterion(criterionName, true);
        const search = structuredClone(getSearchBySelections.value);

        if (orderedDependencyCriterion.includes(criterionName)){
            // We need to remove the lower dependencies from the overall counts search, which would break things.
            const startingIndex = orderedDependencyCriterion.indexOf(criterionName);
            for (let i = startingIndex; i < orderedDependencyCriterion.length; i++) {
                delete search[orderedDependencyCriterion[i]]
            }
        }

        try {
            // The generated SDK's JSDoc types this query as fully populated
            // (YEAR / AUCTIONID / ODOMETERREADING included); this store only ever
            // sends the criterion it actually has selections for.
            const results = await autoAuctionApiSdk.getSearchableOptions(
                criterionName,
                search
            );
            optionsByCriterion.value[criterionName] = results;
            setLoadingByCriterion(criterionName, false);
        } catch (err){
            console.log(err);
        }

        // setTimeout(() => {
        //     if (criterionName === 'MAKE') {
        //         optionsByCriterion.value.MAKE = [
        //             { option: 'Acura', count: 163 },
        //             { option: 'Audi', count: 50 },
        //             { option: 'Buick', count: 456 },
        //             { option: 'BMW', count: 2 },
        //             { option: 'Chrysler', count: 8 },
        //             { option: 'Dodge', count: 34 },
        //             { option: 'Ford', count: 45 },
        //             { option: 'GMC', count: 56 },
        //             { option: 'Honda', count: 678 },
        //             { option: 'Jaguar', count: 78 },
        //             { option: 'Lincoln', count: 89 },
        //             { option: 'Mazda', count: 90 },
        //             { option: 'Toyota', count: 90 },
        //             { option: 'Tesla', count: 4 }
        //         ]
        //     }
        //     if (criterionName === 'MODEL') {
        //         optionsByCriterion.value.MODEL = [
        //             { "option": "Bronco", "count": 248, "MAKE": "Ford", "optionGrouping": "Ford" },
        //             { "option": "Bronco Sport", "count": 290, "MAKE": "Ford", "optionGrouping": "Ford" },
        //             { "option": "Camry", "count": 1071, "MAKE": "Toyota", "optionGrouping": "Toyota" },
        //             { "option": "Escape", "count": 1513, "MAKE": "Ford", "optionGrouping": "Ford" },
        //         ]
        //     }
        //     setLoadingByCriterion(criterionName, false)
        // }, 2000)
    }

    return {
        // Panel Activity
        activeCriterion,
        getActiveCriterion,
        addActiveCriterion,
        removeActiveCriterion,

        // Criterion Vue Component Model
        getOptionsByCriterion,
        getSelectionsByCriterion,
        getLoadingByCriterion,

        // Criterion Component handler for updates
        addCriterionSelection,
        removeCriterionSelection,

        // The big money
        getSearchBySelections
    }
})
